import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { useSelector } from "react-redux";
import useApi from "../../../CustomHooks/useApi";

const USER_TYPES = ["MERCHANT", "DISTRIBUTOR", "MASTER_DISTRIBUTOR"];

const ROLE_STYLE = {
  MERCHANT:           "bg-emerald-100 text-emerald-700",
  DISTRIBUTOR:        "bg-blue-100 text-blue-700",
  MASTER_DISTRIBUTOR: "bg-violet-100 text-violet-700",
};

const UserPicker = ({ value, onChange, onUserSelect, roleFilter, resetKey }) => {
  const [open,  setOpen]  = useState(false);
  const [query, setQuery] = useState(value || "");
  const [users, setUsers] = useState([]);
  const ref = useRef(null);

  const { callApi: fetchUsers, loading } = useApi("/admin/users/users-hierarchy/new/v1/not-of-type", "GET");

  useEffect(() => {
    fetchUsers().then((res) => setUsers(Array.isArray(res) ? res : [])).catch(() => {});
  }, []);

  // Reset query when roleFilter changes (triggered by resetKey)
  useEffect(() => {
    setQuery("");
    setOpen(false);
  }, [resetKey]);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const filtered = useMemo(() => {
    let pool = roleFilter ? users.filter((u) => u.role === roleFilter) : users;
    if (!query.trim()) return pool.slice(0, 20);
    const s = query.toLowerCase();
    return pool.filter((u) =>
      u?.username?.toLowerCase().includes(s) ||
      u?.fullName?.toLowerCase().includes(s) ||
      u?.mobileNumber?.toLowerCase().includes(s)
    ).slice(0, 20);
  }, [query, users, roleFilter]);

  const handleSelect = (user) => {
    setQuery(user.username);
    onChange(user.username);
    onUserSelect && onUserSelect(user);
    setOpen(false);
  };

  return (
    <div ref={ref} className="relative">
      <input
        type="text"
        value={query}
        placeholder="Search name, mobile, username…"
        onFocus={() => setOpen(true)}
        onChange={(e) => { setQuery(e.target.value); onChange(e.target.value); setOpen(true); }}
        className="w-full bg-gray-50 rounded-xl px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-[#0A2A4F] focus:bg-white transition"
      />
      {query && (
        <button
          onClick={() => { setQuery(""); onChange(""); }}
          className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 text-lg leading-none"
        >×</button>
      )}

      {open && (
        <div className="absolute z-30 top-full mt-1 left-0 right-0 bg-white border border-gray-200 rounded-2xl shadow-xl max-h-56 overflow-y-auto">
          {loading ? (
            <p className="text-xs text-gray-400 text-center py-4">Loading users…</p>
          ) : filtered.length === 0 ? (
            <p className="text-xs text-gray-400 text-center py-4">No users found</p>
          ) : (
            filtered.map((user) => (
              <button
                key={user.username}
                onClick={() => handleSelect(user)}
                className="w-full flex items-center justify-between px-4 py-3 hover:bg-gray-50 border-b border-gray-50 last:border-0 text-left transition"
              >
                <div>
                  <p className="text-sm font-bold text-gray-800">{user.fullName || user.username}</p>
                  <p className="text-xs text-gray-400">{user.mobileNumber} · {user.username}</p>
                </div>
                <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${ROLE_STYLE[user.role] || "bg-gray-100 text-gray-500"}`}>
                  {user.role?.replace(/_/g, " ")}
                </span>
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
};

/* Detail popup for a clicked slab */
const SlabDetailPopup = ({ slab, userType, userId, serviceType, onClose }) => {
  if (!slab) return null;

  const rows = [
    { label: "Agent Charge",          value: slab.agentChargeValue, type: slab.agentChargeType, color: "text-orange-600",  bg: "bg-orange-50",  border: "border-orange-200"  },
    { label: "Agent Commission",      value: slab.agentCommValue,   type: slab.agentCommType,   color: "text-emerald-600", bg: "bg-emerald-50", border: "border-emerald-200" },
    { label: "Distributor Commission",value: slab.distCommValue,    type: slab.distCommType,    color: "text-blue-600",    bg: "bg-blue-50",    border: "border-blue-200"    },
    { label: "MD Commission",         value: slab.mdCommValue,      type: slab.mdCommType,      color: "text-violet-600",  bg: "bg-violet-50",  border: "border-violet-200"  },
  ];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" />
      <div
        className="relative bg-white rounded-3xl shadow-2xl w-full max-w-md overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="bg-gradient-to-r from-[#0A2A4F] to-[#0d3562] px-6 py-5">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-xs font-bold text-white/50 uppercase tracking-widest mb-1">{serviceType}</p>
              <h2 className="text-xl font-bold text-white">
                ₹{slab.minAmount?.toLocaleString()} – ₹{slab.maxAmount?.toLocaleString()}
              </h2>
              <div className="flex items-center gap-2 mt-2">
                <span className={`text-xs font-bold px-2.5 py-1 rounded-full ${slab.isActive ? "bg-emerald-400/20 text-emerald-300" : "bg-red-400/20 text-red-300"}`}>
                  {slab.isActive ? "Active" : "Inactive"}
                </span>
                {slab.operator && (
                  <span className="text-xs text-white/50 bg-white/10 px-2.5 py-1 rounded-full">{slab.operator}</span>
                )}
              </div>
            </div>
            <button
              onClick={onClose}
              className="w-8 h-8 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white text-sm transition"
            >
              ✕
            </button>
          </div>
          <div className="flex gap-4 mt-3 pt-3 border-t border-white/10">
            <span className="text-xs text-white/50">User: <span className="text-white font-semibold">{userId}</span></span>
            <span className="text-xs text-white/50">Type: <span className="text-white font-semibold">{userType?.replace(/_/g, " ")}</span></span>
          </div>
        </div>

        {/* Commission rows */}
        <div className="p-5 space-y-3">
          <p className="text-xs font-bold text-gray-400 uppercase tracking-widest">Commission Breakdown</p>

          {rows.map((row) => (
            <div key={row.label} className={`flex items-center justify-between rounded-xl px-4 py-3 border ${row.bg} ${row.border}`}>
              <span className="text-sm font-semibold text-gray-700">{row.label}</span>
              <div className="text-right">
                <span className={`text-lg font-bold ${row.color}`}>{row.value ?? "—"}</span>
                <span className="text-xs text-gray-400 ml-1.5">{row.type}</span>
              </div>
            </div>
          ))}

          {/* TDS section */}
          <div className="mt-1">
            <p className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-2">TDS Percentages</p>
            <div className="grid grid-cols-3 gap-2">
              {[
                ["Merchant", slab.merchantTdsPercentage],
                ["Distributor", slab.distTdsPercentage],
                ["MD", slab.mdTdsPercentage],
              ].map(([label, val]) => (
                <div key={label} className="bg-gray-50 border border-gray-100 rounded-xl p-3 text-center">
                  <p className="text-xs text-gray-400 mb-1">{label}</p>
                  <p className="text-lg font-bold text-gray-800">{val ?? "—"}%</p>
                </div>
              ))}
            </div>
          </div>

          <button
            onClick={onClose}
            className="w-full mt-2 py-3 rounded-xl bg-[#0A2A4F] text-white font-bold text-sm hover:bg-[#0d3562] transition"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

const ApplicableRatePreview = ({ serviceType }) => {
  const [userType,     setUserType]     = useState("MERCHANT");
  const [userId,       setUserId]       = useState("");
  const [selectedUser, setSelectedUser] = useState(null);
  const [slabs,        setSlabs]        = useState([]);
  const [slabLoading,  setSlabLoading]  = useState(false);
  const [selectedSlab, setSelectedSlab] = useState(null);
  const [pickerKey,    setPickerKey]    = useState(0);

  const token = useSelector((s) => s.auth.employeeDetails?.token);

  const fetchSlabs = async (uid, utype) => {
    if (!uid || !utype) return;
    try {
      setSlabLoading(true);
      setSlabs([]);
      const res = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/commission/rates/service/${serviceType}/user-type/${utype}/user/${uid}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const data = res.data;
      setSlabs(Array.isArray(data) ? data : []);
    } catch {
      setSlabs([]);
    } finally {
      setSlabLoading(false);
    }
  };

  const handleUserTypeChange = (newType) => {
    setUserType(newType);
    setUserId("");
    setSelectedUser(null);
    setSlabs([]);
    setPickerKey((k) => k + 1); // resets UserPicker query
  };

  return (
    <div className="space-y-4">
      {/* Input card */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm">
        <div className="px-6 py-4 border-b border-gray-100">
          <h2 className="text-base font-bold text-gray-900">Rate Checker</h2>
          <p className="text-xs text-gray-400 mt-0.5">Select a user to view all configured rate slabs</p>
        </div>

        <div className="p-5">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Service Type */}
            <div>
              <label className="block text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1.5">Service Type</label>
              <div className="bg-gray-50 rounded-xl px-4 py-3 text-sm text-gray-600 font-semibold">{serviceType}</div>
            </div>

            {/* User Type */}
            <div>
              <label className="block text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1.5">User Type</label>
              <select
                value={userType}
                onChange={(e) => handleUserTypeChange(e.target.value)}
                className="w-full bg-gray-50 rounded-xl px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-[#0A2A4F] focus:bg-white transition"
              >
                {USER_TYPES.map((t) => <option key={t} value={t}>{t.replace(/_/g, " ")}</option>)}
              </select>
            </div>

            {/* User ID */}
            <div>
              <label className="block text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1.5">
                User ID
                <span className="ml-1.5 normal-case text-gray-300 font-normal">— {userType.replace(/_/g, " ")} only</span>
              </label>
              <UserPicker
                key={pickerKey}
                resetKey={pickerKey}
                value={userId}
                onChange={(val) => { setUserId(val); if (!val) { setSlabs([]); setSelectedUser(null); } }}
                roleFilter={userType}
                onUserSelect={(user) => {
                  const utype = user.role && USER_TYPES.includes(user.role) ? user.role : userType;
                  setUserId(user.username);
                  setUserType(utype);
                  setSelectedUser(user);
                  fetchSlabs(user.username, utype);
                }}
              />
            </div>
          </div>

          {/* Selected user chip */}
          {selectedUser && (
            <div className="mt-4 flex items-center gap-3 bg-[#0A2A4F]/5 border border-[#0A2A4F]/10 rounded-xl px-4 py-3">
              <div className="w-8 h-8 rounded-full bg-[#0A2A4F] flex items-center justify-center text-white text-xs font-bold flex-shrink-0">
                {(selectedUser.fullName || selectedUser.username)?.[0]?.toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-bold text-gray-800 truncate">{selectedUser.fullName || selectedUser.username}</p>
                <p className="text-xs text-gray-400">{selectedUser.mobileNumber} · {selectedUser.username}</p>
              </div>
              <span className={`px-2.5 py-1 rounded-full text-xs font-bold flex-shrink-0 ${ROLE_STYLE[selectedUser.role] || "bg-gray-100 text-gray-500"}`}>
                {selectedUser.role?.replace(/_/g, " ")}
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Slab cards */}
      {(slabLoading || (userId && slabs.length >= 0 && selectedUser)) && (
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
            <div>
              <h3 className="text-sm font-bold text-gray-900">Rate Slabs</h3>
              <p className="text-xs text-gray-400 mt-0.5">Click any slab to view full commission details</p>
            </div>
            {!slabLoading && (
              <span className="bg-gray-100 text-gray-500 px-2.5 py-0.5 rounded-full text-xs font-bold">
                {slabs.length} slab{slabs.length !== 1 ? "s" : ""}
              </span>
            )}
          </div>

          <div className="p-5">
            {slabLoading ? (
              <div className="py-10 text-center">
                <div className="w-6 h-6 border-2 border-[#0A2A4F] border-t-transparent rounded-full animate-spin mx-auto mb-2" />
                <p className="text-sm text-gray-400">Loading slabs…</p>
              </div>
            ) : slabs.length === 0 ? (
              <div className="py-10 text-center">
                <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center text-2xl mx-auto mb-3">—</div>
                <p className="text-sm font-semibold text-gray-500">No rate slabs configured</p>
                <p className="text-xs text-gray-400 mt-1">No rates have been set for this user under {serviceType}</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
                {slabs
                  .slice()
                  .sort((a, b) => a.minAmount - b.minAmount)
                  .map((slab, i) => (
                    <button
                      key={i}
                      onClick={() => setSelectedSlab(slab)}
                      className="text-left rounded-2xl border-2 border-gray-100 bg-white hover:border-[#0A2A4F]/30 hover:shadow-md transition-all group"
                    >
                      {/* Range header */}
                      <div className="flex items-center justify-between px-4 pt-4 pb-3 border-b border-gray-50">
                        <span className="text-sm font-bold text-gray-800 group-hover:text-[#0A2A4F] transition">
                          ₹{slab.minAmount?.toLocaleString()} – ₹{slab.maxAmount?.toLocaleString()}
                        </span>
                        <div className="flex items-center gap-1.5">
                          {slab.operator && (
                            <span className="text-xs text-gray-400 bg-gray-100 px-2 py-0.5 rounded-full">{slab.operator}</span>
                          )}
                          <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${slab.isActive ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-500"}`}>
                            {slab.isActive ? "Active" : "Inactive"}
                          </span>
                        </div>
                      </div>

                      {/* Commission summary */}
                      <div className="px-4 py-3 space-y-1.5">
                        <div className="flex items-center justify-between">
                          <span className="text-xs text-gray-400">Agent Charge</span>
                          <span className="text-xs font-bold text-orange-600">
                            {slab.agentChargeValue} <span className="text-gray-400 font-normal">({slab.agentChargeType})</span>
                          </span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-xs text-gray-400">Agent Comm</span>
                          <span className="text-xs font-bold text-emerald-600">
                            {slab.agentCommValue} <span className="text-gray-400 font-normal">({slab.agentCommType})</span>
                          </span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-xs text-gray-400">Dist Comm</span>
                          <span className="text-xs font-bold text-blue-600">
                            {slab.distCommValue} <span className="text-gray-400 font-normal">({slab.distCommType})</span>
                          </span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-xs text-gray-400">MD Comm</span>
                          <span className="text-xs font-bold text-violet-600">
                            {slab.mdCommValue} <span className="text-gray-400 font-normal">({slab.mdCommType})</span>
                          </span>
                        </div>
                        <div className="pt-1.5 border-t border-gray-100 flex items-center justify-between">
                          <span className="text-xs text-gray-400">TDS (M / D / MD)</span>
                          <span className="text-xs font-semibold text-gray-600">
                            {slab.merchantTdsPercentage}% / {slab.distTdsPercentage}% / {slab.mdTdsPercentage}%
                          </span>
                        </div>
                      </div>

                      <div className="px-4 pb-3">
                        <span className="block text-center text-xs font-bold text-[#0A2A4F]/40 group-hover:text-[#0A2A4F] transition">
                          Tap to view details →
                        </span>
                      </div>
                    </button>
                  ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Slab detail popup */}
      {selectedSlab && (
        <SlabDetailPopup
          slab={selectedSlab}
          userType={userType}
          userId={userId}
          serviceType={serviceType}
          onClose={() => setSelectedSlab(null)}
        />
      )}
    </div>
  );
};

export default ApplicableRatePreview;
